import ChevronDown20 from '@carbon/icons-react/es/chevron--down/20';
import Search16 from '@carbon/icons-react/es/search/16';
import { useBoolean, useClickAway } from 'ahooks';
import { Dropdown } from 'antd';
import React, {
  forwardRef,
  MouseEvent,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { components } from 'react-select';
import styled from 'styled-components';
import { ProjectId, TaskId, UserType } from 'api/models';
import type { ProjectPhase } from 'api/project';
import { Task, TaskAPI } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Menu as MenuAtom } from 'components/atoms/dropdown';
import {
  CustomDueDatePicker,
  DateValue,
} from 'components/atoms/due-date-picker';
import { TextArea } from 'components/atoms/input';
import { LabelForGhostInput } from 'components/atoms/label';
import { Select, UserTag as UserTagSelect } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { Option as SelectOption } from 'components/atoms/select/types';
import {
  Col,
  Container,
  Footer,
  Popover,
  Row,
  Rte,
  SelectContainer,
} from 'components/molecules/new-task-modal/styled';
import { TaskAssigneePicker } from 'components/molecules/squad-picker/assignee-picker';
import { Option } from 'components/molecules/squad-picker/types';
import { optionsToSquadPayload } from 'components/molecules/squad-picker/utils';
import { ToastDescription, toastService } from 'components/molecules/toast';
import { SprintType } from 'components/organisms/sprint-planning/types';
import { useAddTaskToSprint } from 'components/organisms/sprint-planning/use-add-task-to-sprint';
import { ProductiveHeading05 } from 'components/styles';
import { FetchStatus } from 'data/core';
import { saveTasks } from 'data/tasks/slice';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { getUsersByIds, getUsersNotInProject } from 'utils/idb-utils';
import { useCustomInviteUserToProjectConfirmation } from '../squad-picker/use-invite-user';
import { GoToProjects } from './go-to-projects';

const useAssigneeChange = () => {
  const inviteUser = useCustomInviteUserToProjectConfirmation();

  return useCallback(
    async (projectId: number, selectedUserIds: number[]) => {
      const selectedUsers = await getUsersByIds(selectedUserIds);
      const notInProject = await getUsersNotInProject(projectId);
      const userIdNeedsInvitation = Object.keys(selectedUsers)
        .filter((id) => {
          return notInProject.find((notUser) => notUser.userId === Number(id));
        })
        .map(Number);

      return new Promise<void>((res, rej) => {
        if (userIdNeedsInvitation.length === 0) {
          res();

          return undefined;
        }
        const nonProjectUser = userIdNeedsInvitation.map((userId) => ({
          extra: { id: userId },
          userType: selectedUsers[userId].userType,
        }));

        inviteUser(
          nonProjectUser,
          selectedUserIds,
          selectedUserIds,
          () => res(),
          rej,
          ProjectId(projectId),
        );

        return undefined;
      });
    },
    [inviteUser],
  );
};

const ControlComponent = (props) => <components.Control {...props} />;
const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Search16 />
    </components.DropdownIndicator>
  );
};

const StyledMenu = styled(components.Menu)`
  box-shadow: none;
`;

const Menu = (props) => {
  return <StyledMenu {...props} />;
};

export const TaskName = styled(TextArea)`
  && {
    background: var(--white-100);
    resize: none;
    ${ProductiveHeading05};
    padding-right: 20%;
  }
  border: none;
  margin-bottom: var(--layout-01);
`;

const UserTag = styled(UserTagSelect)`
  overflow: hidden;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  & button:not(:first-child) {
    margin-left: 1px;
  }
`;

const ButtonGroupWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin-top: var(--layout-01);
  gap: var(--spacing-04);
`;

export type StartAndDueDate = [DateValue, DateValue];
export interface NewTaskCardExtraDetails {
  projectPhase?: Pick<ProjectPhase, 'projectPhaseId'>;
  defaultTimeEntryCategory?: number;
  fields?: any[];
}
interface NewTaskFormProps {
  taskName: string;
  setTaskName: (value: string) => void;
  assignee: Option[];
  setAssignee: (value: Option[]) => void;
  startAndDueDate: StartAndDueDate;
  setStartAndDueDate: (value: StartAndDueDate) => void;
  taskDescription: string;
  setTaskDescription: (value: string) => void;
  onSuccess: (result: Task | null) => void;
  onClose?: (e: MouseEvent) => void;
  extraDetails?: NewTaskCardExtraDetails;
  forceCheckAssigneeOnCreate?: boolean;
  isCreateDropdownOptionsShown?: boolean;
}

export interface NewTaskValueSetterProp {
  setProject: (project: SelectOption) => void;
}

export const NewTaskForm = forwardRef<NewTaskValueSetterProp, NewTaskFormProps>(
  (props, ref): JSX.Element => {
    const {
      onSuccess,
      taskDescription = '',
      setTaskDescription,
      taskName = '',
      setTaskName,
      assignee,
      setAssignee,
      startAndDueDate,
      setStartAndDueDate,
      extraDetails,
      forceCheckAssigneeOnCreate,
      isCreateDropdownOptionsShown = true,
    } = props;
    const taskDescLabel = useTranslation('task.description.placeholder');
    const taskNameLabel = useTranslation('task_name');
    const projectLabel = useTranslation('projects.title');
    const createText = useTranslation('create');
    const cancelText = useTranslation('cancel');
    const { user, projects } = useUser();
    const options = (projects ?? []).map((p) => ({
      label: p.projectName || '',
      value: p.projectId,
      imageUrl: p.customer.companyLogoUrl,
    }));

    const { isTaskAddedToSprint, sprint } = useAddTaskToSprint();

    const [selectedProject, setSelectedProject] =
      useState<SelectOption | undefined>();
    const [visible, { toggle, setFalse }] = useBoolean(false);

    const clearFormData = () => {
      setAssignee([]);
      setTaskDescription('');
      setTaskName('');
      setStartAndDueDate([null, null]);
    };

    const handleChange = (v) => {
      setSelectedProject(v);
      setFalse();
    };

    const dispatch = useDispatch();

    useImperativeHandle(ref, () => ({
      setProject: (project) => {
        setSelectedProject(project);
      },
    }));

    const ref1 = useRef(null);
    const ref2 = useRef(null);

    const [isRteFocused, setIsRteFocused] = useState(false);

    useClickAway(() => {
      if (visible) {
        toggle();
      }
    }, [ref1, ref2]);

    const navigateToTask = useNavigateToTask();
    const inviteUser = useAssigneeChange();
    const request = async ({ projectId, data }) => {
      if (data.assignee?.users?.length && forceCheckAssigneeOnCreate) {
        await inviteUser(
          projectId,
          data.assignee?.users.map((u) => u.userId),
        );
      }

      return TaskAPI.createTaskWithForm({
        projectId,
        data: { ...data, ...extraDetails },
      });
    };

    const notificationTitle = useTranslation('notification.task.created');
    const notificationMessage = useTranslation(
      'notification.task.created.message',
    );

    const view = useTranslation('view');

    const showSuccessNotification = ({
      onClick,
    }: {
      onClick?: () => void;
    } = {}) => {
      return toastService({
        title: notificationTitle,
        message: (
          <ToastDescription
            buttonText={view}
            message={notificationMessage}
            onClick={onClick}
          />
        ),
      }).success();
    };

    const { isLoading: loading, mutateAsync: createAndOpenTask } = useMutation(
      request,
      {
        onSuccess: (result) => {
          if (result) {
            dispatch(
              saveTasks({ data: [result], fetchStatus: FetchStatus.Loaded }),
            );
            clearFormData();
            onSuccess(result);
            showSuccessNotification();
            navigateToTask(TaskId(result.project.projectId, result.taskId));
          }
        },
      },
    );

    const { isLoading: creating, mutateAsync: createTask } = useMutation(
      request,
      {
        onSuccess: (result) => {
          if (result) {
            dispatch(
              saveTasks({ data: [result], fetchStatus: FetchStatus.Loaded }),
            );

            clearFormData();
            onSuccess(result);
            showSuccessNotification({
              onClick: () =>
                navigateToTask(TaskId(result.project.projectId, result.taskId)),
            });
          }
        },
      },
    );

    const getFormData = () => ({
      taskDescription,
      taskName,
      assignee: optionsToSquadPayload(assignee),
      startDate: startAndDueDate && startAndDueDate[0],
      dueDate: startAndDueDate && startAndDueDate[1],
      currentSprint:
        isTaskAddedToSprint && sprint?.sprintId
          ? { sprintId: Number(sprint.sprintId) }
          : undefined,
    });
    const { hasAccessToResource } = useWithAccess();

    const projectDetail = useProjectAsRBACResource(
      selectedProject?.value as number,
    );

    const canCreateTask = hasAccessToResource({
      rule: 'TASK:CREATE',
      resourceData: projectDetail,
    });
    const canCreatePrivateTask = hasAccessToResource({
      rule: 'TASK:CREATE',
      actionScope: ['PRIVATE'],
      resourceData: projectDetail,
    });

    const menu = (
      <MenuAtom>
        <MenuAtom.Item
          data-cy="modals.create-task.create-and-open"
          key="1"
          onClick={() =>
            selectedProject &&
            createAndOpenTask({
              projectId: selectedProject.value,
              data: getFormData(),
            })
          }
        >
          <FormattedMessage id="create_and_open" />
        </MenuAtom.Item>
        {user.userType === UserType.Native && canCreatePrivateTask && (
          <MenuAtom.Item
            data-cy="modals.create-task.create-private"
            key="create_private_task"
            onClick={() =>
              selectedProject &&
              createAndOpenTask({
                projectId: selectedProject.value,
                data: {
                  ...getFormData(),
                  private: true,
                },
              })
            }
          >
            <FormattedMessage id="create_as_private" />
          </MenuAtom.Item>
        )}
      </MenuAtom>
    );
    const hasTaskName = taskName.trim().length > 0;

    return projects.length > 0 ? (
      <Container>
        <TaskName
          data-cy="modals.create-task.task-name"
          autoFocus
          placeholder={taskNameLabel}
          autoSize={{ minRows: 1, maxRows: 3 }}
          onChange={(e) => setTaskName(e.target.value)}
          defaultValue={taskName}
          onKeyDown={(event) => {
            if (event.key === 'Tab') {
              if (!visible) {
                toggle();
              }
            }
          }}
        />
        <Row>
          <Col>
            <LabelForGhostInput>{projectLabel}</LabelForGhostInput>
            <Popover
              placement="bottomLeft"
              visible={visible}
              destroyTooltipOnHide
              content={
                <SelectContainer ref={ref1}>
                  <Select
                    type="user"
                    menuIsOpen
                    autoFocus
                    controlShouldRenderValue={false}
                    components={{
                      Control: ControlComponent,
                      DropdownIndicator,
                      Menu,
                      MenuList: VirtualizedMenuList,
                      IndicatorSeparator: () => null,
                    }}
                    placeholder={<FormattedMessage id="placeholder.search" />}
                    options={options}
                    onChange={handleChange}
                    onBlur={setFalse}
                  />
                </SelectContainer>
              }
              trigger="click"
            >
              <Button
                data-cy="modals.create-task.project"
                ref={ref2}
                type="link"
                onClick={() => toggle()}
                disabled={sprint?.sprintType === SprintType.SingleProject}
              >
                {selectedProject ? (
                  <UserTag
                    imageUrl={selectedProject.imageUrl}
                    label={selectedProject.label}
                  />
                ) : (
                  <FormattedMessage id="choose" />
                )}
              </Button>
            </Popover>
          </Col>
          {selectedProject ? (
            <>
              <Col>
                <LabelForGhostInput>
                  <FormattedMessage id="assign_to" />
                </LabelForGhostInput>
                <TaskAssigneePicker
                  projectId={ProjectId(Number(selectedProject.value))}
                  value={assignee}
                  onChange={setAssignee}
                  hidePlaceholderSection
                  startDate={startAndDueDate[0]}
                  dueDate={startAndDueDate[1]}
                />
              </Col>
              <Col>
                <LabelForGhostInput>
                  <FormattedMessage id="due_date" />
                </LabelForGhostInput>
                <CustomDueDatePicker
                  block
                  onDateChange={setStartAndDueDate}
                  startDate={startAndDueDate[0]}
                  dueDate={startAndDueDate[1]}
                />
              </Col>
            </>
          ) : null}
        </Row>
        {selectedProject ? (
          <Rte
            projectId={ProjectId(Number(selectedProject.value))}
            onChange={setTaskDescription}
            data={taskDescription}
            placeholder={taskDescLabel}
            onFocus={() => setIsRteFocused(true)}
            onBlur={() => setIsRteFocused(false)}
            isRteFocused={isRteFocused}
          />
        ) : null}

        <Footer>
          <ButtonGroupWrapper>
            <ButtonGroup>
              <Button
                data-cy="modals.create-task.create"
                type="primary"
                disabled={!hasTaskName || !selectedProject || loading}
                loading={creating || loading}
                onClick={() =>
                  selectedProject &&
                  createTask({
                    projectId: selectedProject.value,
                    data: getFormData(),
                  })
                }
              >
                {createText}
              </Button>
              {!creating && !loading && isCreateDropdownOptionsShown && (
                <Dropdown
                  overlay={menu}
                  disabled={
                    creating ||
                    !hasTaskName ||
                    !selectedProject ||
                    loading ||
                    !canCreateTask
                  }
                >
                  <Button
                    data-cy="modals.create-task.more"
                    type="primary"
                    icon={<ChevronDown20 />}
                    loading={loading}
                  />
                </Dropdown>
              )}
            </ButtonGroup>
            <Button
              data-cy="modals.create-task.cancel"
              type="tertiary"
              disabled={loading}
              loading={creating || loading}
              onClick={(e) => {
                e.stopPropagation();
                props.onClose?.(e as any);
              }}
            >
              {cancelText}
            </Button>
          </ButtonGroupWrapper>
        </Footer>
      </Container>
    ) : (
      <GoToProjects onClick={() => onSuccess(null)} />
    );
  },
);
